import React, { useEffect } from 'react';
import { LocalizedToastText } from './LocalizedToastText';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { useAppDispatch, useAppSelector } from '@store/index';
import { removeNotification, selectNotifications, selectTheme } from '@features/ui/uiSlice';
import { LAYOUT, Z_INDEX } from '@constants/layout';
import type { Notification as AppNotification } from '@features/ui/uiSlice';

const SHOW = {
  success: toast.success,
  error: toast.error,
  warning: toast.warning,
  info: toast.info,
} as const;

/**
 * Renders the Redux notification queue through sonner. The store stays the
 * entry point so every existing dispatch(showNotification(...)) keeps working;
 * each notification is handed to sonner once and then dropped from the queue,
 * which owns its timing and dismissal from that point.
 */
const NotificationManager: React.FC = () => {
  const dispatch = useAppDispatch();
  const notifications = useAppSelector(selectNotifications);
  const theme = useAppSelector(selectTheme);

  useEffect(() => {
    notifications.forEach((notification: AppNotification) => {
      // One line: the message is the toast. Severity is carried by the icon and
      // the rich colour, plus an sr-only word so it is not colour-only.
      const body = notification.messageKey
        ? <LocalizedToastText messageKey={notification.messageKey} />
        : notification.message;
      (SHOW[notification.type] ?? toast.info)(body, {
        duration: notification.duration,
        description: (
          <span className="sr-only">
            <LocalizedToastText messageKey={`notifications.${notification.type}`} />
          </span>
        ),
      });
      dispatch(removeNotification(notification.id));
    });
  }, [notifications, dispatch]);

  return (
    <Toaster
      position="top-right"
      // Per side: clear the app header, but keep the usual 16px gutter on the
      // right. A single value applies to every edge and pushed the toast a
      // header's width in from the edge.
      offset={{ top: LAYOUT.HEADER_HEIGHT + 16, right: 16 }}
      richColors
      closeButton
      theme={theme === 'dark' ? 'dark' : 'light'}
      // sonner mounts its own container on <body>, outside the shell, so it
      // needs the island class and a layer above the Radix portals.
      className="revy-ui"
      style={{ zIndex: Z_INDEX.NOTIFICATION }}
      toastOptions={{
        classNames: {
          title: 'revy-cjk leading-[1.5]',
          description: 'revy-cjk leading-[1.6]',
        },
      }}
    />
  );
};

export default NotificationManager;
